import {
  Box,
  ButtonGroup,
  Button,
  Typography,
  Popper,
  Grow,
  ClickAwayListener,
  MenuList,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import { DashboardActionsProps } from './DahboardActions.types'
import { useEffect, useRef, useState } from 'react'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { MenuItem, Paper } from '@mui/material'
import { useIsReadOnly } from 'Common/Hooks/useIsReadOnly.hooks'
import { usePlanBuiltByV2Engine } from 'Common/Charts/StockWaterfall/stockWaterfall.data'

// `notOnV2`: the item is disabled on plans built by the V2 engine, with this
// tooltip.
const options: { label: string; notOnV2?: string }[] = [
  { label: 'Edit capacity' },
  {
    label: 'Auto Bow Wave',
    notOnV2: 'Not available on plans built by the V2 engine yet',
  },
  { label: 'Edit settings' },
  {
    label: 'Alternative workcentres',
    notOnV2: 'Use the Planned production tab for plans built by the V2 engine',
  },
  {
    label: 'Edit production',
    notOnV2: 'Use the Planned production tab for plans built by the V2 engine',
  },
]

let enabled = false
export const DashboardActionsView = ({
  handleBowWaveClick,
  handleEditCapacityClick,
  handleEditSettingsClick,
  handleAlternativeWorkcentres,
  handleEditProduction,
  planId,
  workcentreId,
}: DashboardActionsProps) => {
  const anchorRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const { readOnly, isPlanReadOnlyLoading } = useIsReadOnly(planId)
  // Same plan-row query as useIsReadOnly: no extra request, and the spinner
  // below covers its loading, so the item is never briefly enabled.
  const { builtByV2Engine } = usePlanBuiltByV2Engine(planId)

  const handleClick = () => {
    setOpen((prevOpen) => !prevOpen)
  }
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = (event: Event) => {
    if (anchorRef?.current?.contains(event.target as HTMLElement)) {
      return
    }

    setOpen(false)
  }

  const handleMenuItemClick = async (index: number) => {
    setOpen(false)
    if (index === 0) {
      handleEditCapacityClick()
    } else if (index === 1) {
      handleBowWaveClick()
    } else if (index === 2) {
      handleEditSettingsClick()
    } else if (index === 3) {
      handleAlternativeWorkcentres()
    } else if (index === 4) {
      handleEditProduction()
    }
  }

  enabled = !readOnly

  if (isPlanReadOnlyLoading) {
    return <CircularProgress color="inherit" size={16} />
  }

  return (
    <Box>
      <>
        <ButtonGroup
          disabled={readOnly}
          ref={anchorRef}
          aria-label="split button"
          variant={enabled ? 'contained' : 'outlined'}
          sx={{
            cursor: 'pointer',
            textDecoration: 'none',
            fontSize: '16px',
            fontWeight: 'bold',
            borderRadius: '0 !important',
          }}
        >
          <Button
            onClick={handleClick}
            variant={enabled ? 'contained' : 'outlined'}
            sx={{
              backgroundColor: enabled ? '#00415A !important' : '#fafafa',
              color: enabled ? '#FFF' : '#000',
              textTransform: 'none',
              borderRadius: '0',
              height: '100%',
              fontWeight: 'bold',
            }}
          >
            <Typography
              sx={{
                fontWeight: 'bold',
              }}
              variant="button"
            >
              Actions
            </Typography>
          </Button>
          <Button
            variant={enabled ? 'contained' : 'outlined'}
            sx={{
              backgroundColor: enabled ? '#00415A !important' : '#fafafa',
              color: enabled ? '#FFF' : '#000',
              textTransform: 'none',
              borderRadius: '0',
            }}
            size="small"
            aria-controls={open ? 'split-button-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-label="select merge strategy"
            aria-haspopup="menu"
            onClick={handleToggle}
          >
            <ArrowDropDownIcon />
          </Button>
        </ButtonGroup>
        <Popper
          sx={{
            zIndex: 10,
          }}
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === 'bottom' ? 'center top' : 'center bottom',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="split-button-menu" autoFocusItem>
                    {options.map(({ label, notOnV2 }, index) => {
                      const refused = Boolean(notOnV2) && builtByV2Engine
                      const item = (
                        <MenuItem
                          key={label}
                          disabled={refused}
                          onClick={() => handleMenuItemClick(index)}
                        >
                          <Typography
                            sx={{
                              fontWeight: 'bold',
                            }}
                            variant="body1"
                          >
                            {label}
                          </Typography>
                        </MenuItem>
                      )
                      // A disabled item gets no hover, so its tooltip wraps a
                      // span. Only refused items are wrapped: MenuList passes
                      // autoFocus to its first child (Edit capacity), which
                      // must stay a MenuItem.
                      return refused ? (
                        <Tooltip key={label} title={notOnV2} placement="top">
                          <span>{item}</span>
                        </Tooltip>
                      ) : (
                        item
                      )
                    })}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </>
    </Box>
  )
}
